import { useEffect, useMemo, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BsFiletypeCsv } from 'react-icons/bs';
import { FaFilePdf } from 'react-icons/fa6';
import { MdCheck, MdOutlineClose } from 'react-icons/md';
import { RiDeleteBinLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';
import AssetCard, { getWorkOrderAssetType, getWorkOrderAssetTypeLabel } from '../../components/AssetCard';
import Breadcrumbs from '../../components/Breadcrumbs';
import CustomModal from '../../components/CustomModal';
import FilterExternalDropdown from '../../components/FilterExternalDropdown';
import VehicleFilterDropdown from '../../components/VehicleFilterDropdown';
import UserFilterDropdown from '../../components/UserFilterDropdown';
import InlineUserCell from '../../components/InlineUserCell';
import MultiLineText from '../../components/MultiLineText';
import NewTableComponent from '../../components/NewTableComponent';
import Notification from '../../components/Notification';
import PermissionWrapper from '../../components/PermissionWrapper';
import TableHoverCard from '../../components/TableHoverCard';
import TablePopover from '../../components/TablePopover';
import UserAvatar from '../../components/UserAvatar';
import Button from '../../components/button';
import { createLinkIssueApi, deleteIssue, fetchIssues, fetchIssueVehiclesDropdown, fetchIssueEquipmentDropdown, getIssuesOverview, getIssueTagsDropdown, getServiceEntryDropdown, issueExcelExport, issuePdfExport, updateAssigneeApi, updatePriorityApi, updateStatusApi, updateTagsApi } from '../../store/Slices/IssuesSlice';
import { fetchNotificationsData } from '../../store/Slices/NotificationSlice';
import { getUserDropdown } from '../../store/Slices/UserSlice';
import '../../styles/Services.css';
import { priorityOptions, statusOptions } from '../../utils/Dropdowns';
import { AppMainModules, IssuePermission, MaintinaceSubModules } from '../../utils/app-constant';
import { hasPermission } from '../../utils/helper';
import { closedIcon, exportDataIcon, highPriorityIcon, inProgressIcon, lowPriorityIcon, mediumPriorityIcon, openIcon, pendingIcon, plusIcon, resolvedIcon } from '../../utils/svgs';
import { errorNotify, successNotify } from '../../utils/toast';
import CreateIssue from './CreateIssue';
import IssuesBoxChart from './IssuesBoxChart';

const issueStatusOptions = statusOptions.filter((status) => status.value !== 'completed');

const assetTypeOptions = [
    { label: 'Vehicle', value: 'vehicle' },
    { label: 'Equipment', value: 'equipment' },
];

const ClearIndicator = (props) => {
    const { onTickClick } = props;
    const {
        children = <span style={{ fontSize: "18px", cursor: "pointer" }}><MdOutlineClose style={{ color: "#323232" }} /></span>,
        innerProps: { ref, ...restInnerProps },
    } = props;

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "0px", marginLeft: "5px", marginRight: "4px" }}>
            <div
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onTickClick) onTickClick();
                }}
                tabIndex={-1}
                style={{
                    cursor: "pointer",
                    marginRight: "4px",
                    color: "#323232",
                }}
            >
                <MdCheck style={{ fontSize: "18px" }} />
            </div>
            <div {...restInnerProps} ref={ref}>
                {children}
            </div>
        </div>
    );
};

const Issues = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { issuesData, issuesLoading, deleteIssueLoading, issuePdfExportLoading, updateTagsLoading,
        issueExcelExportLoading, createLinkIssueLoading, updateAssigneeLoading, getIssueTagsDropdownLoading,
        getIssueTagsDropdownData, issueVehiclesDropdownData, issueEquipmentDropdownData } = useSelector((state) => state.issues)
    const { userDropdownData } = useSelector((state) => state.users)

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchText, setSearchText] = useState('');

    const [deleteUserPopup, setDeleteUserPopup] = useState(false);
    const [deleteUserId, setDeleteUserId] = useState(null);

    const [showExportOption, setShowExportOption] = useState(false);
    const [searchFilters, setSearchFilters] = useState(null);
    const [showCreateIssuePopup, setShowCreateIssuePopup] = useState(false);

    const [priorityPopup, setPriorityPopup] = useState({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
    const [sePopup, setSePopup] = useState({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
    const [tagsPopup, setTagsPopup] = useState({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
    const [selectedSE, setSelectedSE] = useState({});
    const [statusPopup, setStatusPopup] = useState({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
    const [hoveredUser, setHoveredUser] = useState({ show: false, rowId: null, type: null, anchorPos: { x: 0, y: 0 }, locked: false });
    const hoverTimeoutRef = useRef(null);
    const [reporterDropdown, setReporterDropdown] = useState({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
    const [assigneeDropdown, setAssigneeDropdown] = useState({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });

    const [showModal, setShowModal] = useState(false);

    const [tableLoading, setTableLoading] = useState(false);
    const [tagsArr, setTagsArr] = useState({});

    const canCreate = hasPermission({ permissionSlug: IssuePermission.Create, menuSlug: MaintinaceSubModules.Issue, moduleSlug: AppMainModules.maintenance });
    const canDelete = hasPermission({ permissionSlug: IssuePermission.Delete, menuSlug: MaintinaceSubModules.Issue, moduleSlug: AppMainModules.maintenance });
    const canView = hasPermission({ permissionSlug: IssuePermission.View, menuSlug: MaintinaceSubModules.Issue, moduleSlug: AppMainModules.maintenance });
    const canEdit = hasPermission({ permissionSlug: IssuePermission.Update, menuSlug: MaintinaceSubModules.Issue, moduleSlug: AppMainModules.maintenance });
    const canExport = hasPermission({ permissionSlug: IssuePermission.Export, menuSlug: MaintinaceSubModules.Issue, moduleSlug: AppMainModules.maintenance });

    const popupRef = useRef();
    const sePopupRef = useRef();
    const statusPopupRef = useRef();
    const tagsPopupRef = useRef();
    const reporterDropdownRef = useRef();
    const assigneeDropdownRef = useRef();

    useEffect(() => {
        dispatch(getServiceEntryDropdown())
        dispatch(getUserDropdown());
        dispatch(fetchIssueVehiclesDropdown());
        dispatch(fetchIssueEquipmentDropdown());
        dispatch(getIssueTagsDropdown())
        dispatch(fetchNotificationsData());
    }, [])

    const tagsOptions = getIssueTagsDropdownData?.map(tag => ({ label: tag.tag_name, value: tag.tag_name }))
    const vehicleOptions = useMemo(() => {
        return (issueVehiclesDropdownData || []).map((v) => ({
            label: v.registration_number || v.name || v.label || String(v.id || v.value),
            value: v.id ?? v.value,
        }));
    }, [issueVehiclesDropdownData]);

    const equipmentOptions = useMemo(() => {
        return (issueEquipmentDropdownData || []).map((e) => ({
            label: e.name || e.equipment_name || e.label || String(e.id || e.value),
            value: e.id ?? e.value,
        }));
    }, [issueEquipmentDropdownData]);


    useEffect(() => {
        function handleClickOutside(event) {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setPriorityPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
            }
        }
        if (priorityPopup.open) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [priorityPopup.open]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (sePopupRef.current && !sePopupRef.current.contains(event.target)) {
                setSePopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
                setSelectedSE({});
            }
        }
        if (sePopup.open) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [sePopup.open]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (statusPopupRef.current && !statusPopupRef.current.contains(event.target)) {
                setStatusPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
            }
        }
        if (statusPopup.open) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [statusPopup.open]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (tagsPopupRef.current && !tagsPopupRef.current.contains(event.target)) {
                setTagsPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
            }
        }
        if (tagsPopup.open) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [tagsPopup.open]);

    useEffect(() => {
        if (searchText.length > 0 || searchFilters) {
            dispatch(fetchIssues({
                page: currentPage,
                limit: limit,
                search_text: searchText,
                searchFilters: searchFilters
            }));
        }
        else {
            dispatch(fetchIssues({
                page: currentPage,
                limit: limit,
            }));
        }
    }, [searchText, currentPage, limit, searchFilters]);

    const commonColumns = {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
    }

    const columns = [
        {
            ...commonColumns,
            name: "Issue ID",
            key: "id",
            selector: (row) => (
                <span
                    className="hyperlink"
                    onClick={canView ? () => navigate(`/issue/issue-details/${row?.id}`) : undefined}
                    style={{ cursor: canView ? 'pointer' : 'default' }}
                >
                    {row?.service_issue_id}
                </span>
            ),
            width: "100px",
            tableColumn: false,
            filterName: "issueIdFilter",
            type: "text",
        },
        {
            ...commonColumns,
            name: "Asset Type",
            key: "asset_type",
            selector: (row) => {
                const assetType = getWorkOrderAssetType(row);
                return (
                    <span style={{ textTransform: "capitalize" }}>
                        {assetType || '-'}
                    </span>
                );
            },
            modifyData: (row) => getWorkOrderAssetType(row),
            width: "120px",
            tableColumn: true,
            filterName: "assetTypeFilter",
            type: "text",
            filterComponent: FilterExternalDropdown,
            isMulti: true,
            options: assetTypeOptions,
        },
        {
            ...commonColumns,
            name: "Vehicle",
            key: "vehicle",
            selector: (row, index) => {
                const assetType = getWorkOrderAssetType(row);
                if (assetType !== 'vehicle') return <span className="no-items">-</span>;
                const label = row?.vehicle?.registration_number || row?.vehicle?.reg_number || row?.vehicle?.name || '-';
                return (
                    <div
                        className="hyperlink"
                        onMouseEnter={e => handleUserMouseEnter(e, row, 'vehicle')}
                        onMouseLeave={handleUserMouseLeave}
                        onClick={e => handleUserClick(e, row, 'vehicle')}
                        style={{ display: "inline-block", overflow: "visible" }}
                    >
                        {label}
                        <TableHoverCard
                            show={hoveredUser.show && hoveredUser.rowId === row.id && hoveredUser.type === 'vehicle'}
                            index={index}
                            placement="right"
                            cardWidth={320}
                            popupHeight={336}
                            offsetX={12}
                            anchorRect={hoveredUser.anchorPos}
                            portalToBody
                            viewportPadding={16}
                            zIndex={10000}
                            onMouseEnter={handleCardMouseEnter}
                            onMouseLeave={handleCardMouseLeave}
                        >
                            {hoveredUser.show && hoveredUser.rowId === row.id && hoveredUser.type === 'vehicle' && (
                                <AssetCard workOrder={row} />
                            )}
                        </TableHoverCard>
                    </div>
                );
            },
            modifyData: (row) => row?.vehicle?.registration_number || row?.vehicle?.reg_number || row?.vehicle?.name || '',
            width: "160px",
            tableColumn: true,
            filterName: "vehicleFilter",
            type: "select",
            isMulti: true,
            filterComponent: VehicleFilterDropdown,
            options: vehicleOptions,
            columnName: "Vehicle",
        },
        {
            ...commonColumns,
            name: "Equipment",
            key: "equipment",
            selector: (row, index) => {
                const assetType = getWorkOrderAssetType(row);
                if (assetType !== 'equipment') return <span className="no-items">-</span>;
                const label = row?.equipment?.registration_number || row?.equipment?.equipment_id || row?.equipment?.name || row?.equipment?.serial_number || '-';
                return (
                    <div
                        className="hyperlink"
                        onMouseEnter={e => handleUserMouseEnter(e, row, 'equipment')}
                        onMouseLeave={handleUserMouseLeave}
                        onClick={e => handleUserClick(e, row, 'equipment')}
                        style={{ display: "inline-block", overflow: "visible" }}
                    >
                        {label}
                        <TableHoverCard
                            show={hoveredUser.show && hoveredUser.rowId === row.id && hoveredUser.type === 'equipment'}
                            index={index}
                            placement="right"
                            cardWidth={320}
                            popupHeight={336}
                            offsetX={12}
                            anchorRect={hoveredUser.anchorPos}
                            portalToBody
                            viewportPadding={16}
                            zIndex={10000}
                            onMouseEnter={handleCardMouseEnter}
                            onMouseLeave={handleCardMouseLeave}
                        >
                            {hoveredUser.show && hoveredUser.rowId === row.id && hoveredUser.type === 'equipment' && (
                                <AssetCard workOrder={row} />
                            )}
                        </TableHoverCard>
                    </div>
                );
            },
            modifyData: (row) => row?.equipment?.registration_number || row?.equipment?.equipment_id || row?.equipment?.name || '',
            width: "160px",
            tableColumn: true,
            filterName: "equipmentFilter",
            type: "select",
            isMulti: true,
            filterComponent: VehicleFilterDropdown,
            options: equipmentOptions,
            columnName: "Equipment",
        },
        {
            ...commonColumns,
            name: "Title & Description",
            key: "description",
            tableColumn: false,
            selector: (row) => {
                // const plainText = stripHtml(row?.description);
                // const preview = plainText.length > 30 ? plainText.slice(0, 30) + '...' : plainText;

                const summaryText = row?.summery?.length > 30 ? row?.summery?.slice(0, 30) + '...' : row?.summery
                return (
                    <div>
                        <div>
                            <span className="hyperlink"
                                onClick={canView ? () => navigate(`/issue/issue-details/${row?.id}`) : undefined}
                                style={{ cursor: canView ? 'pointer' : 'default' }}
                            >
                                {summaryText}</span>
                        </div>
                        {/* {plainText ? (
                            <div style={{ marginTop: 4 }}>
                                <span>{preview} </span>
                            </div>
                        ) : <div className='no-items'>No Description</div>} */}
                    </div>
                );
            },
            width: "250px",
        },
        {
            ...commonColumns,
            name: "Tags",
            key: "tags",
            tableColumn: false,
            filterName: "tagsFilter",
            type: "text",
            selector: (row, index) => {
                const serviceItems = row?.issue_tags || [];

                const value = Array.isArray(tagsArr[row.id])
                    ? tagsArr[row.id]
                    : (row.issue_tags || []).map(tag => ({
                        label: tag.tag_name,
                        value: tag.tag_name
                    }));

                return (
                    <div style={{ display: "inline-block", overflow: "visible" }}>
                        {(tagsPopup.open && tagsPopup.rowId === row.id) ? (
                            <>
                                {(updateTagsLoading || issuesLoading) ? (
                                    <div>Saving...</div>
                                ) : (
                                    <div style={{ position: "relative" }}>
                                        <CreatableSelect
                                            isLoading={getIssueTagsDropdownLoading}
                                            menuIsOpen={tagsPopup.forceClose ? false : undefined}
                                            options={tagsOptions}
                                            placeholder={"Type Tags..."}
                                            menuPosition="fixed"
                                            isMulti
                                            styles={{
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                indicatorSeparator: () => ({ display: "none" }),
                                                dropdownIndicator: () => ({ display: "none" }),
                                                container: (base) => ({
                                                    ...base,
                                                    marginTop: "5px",
                                                    fontSize: "12px",
                                                }),
                                                control: (base, state) => ({
                                                    ...base,
                                                    minWidth: "150px",
                                                    maxWidth: "150px",
                                                    overflowX: "auto",
                                                    whiteSpace: "nowrap",
                                                    minHeight: "34px",
                                                    maxHeight: "34px",
                                                    borderColor: "#D9D9D9",
                                                    boxShadow: "none",
                                                    borderColor: state.isFocused ? "#ccc" : base.borderColor,
                                                    "&:hover": {
                                                        borderColor: "#ccc",
                                                    },
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    flexWrap: "nowrap",
                                                    overflowX: "auto",
                                                }),
                                                multiValue: (base) => ({
                                                    ...base,
                                                    flex: "0 0 auto",
                                                }),
                                            }}
                                            isClearable
                                            formatCreateLabel={(userInput) => `Create "${userInput}"`}
                                            noOptionsMessage={() => "create tag"}
                                            defaultValue={value}
                                            onChange={(selected) => handleTagsChange(selected, row)}
                                            components={{
                                                ClearIndicator: (props) => (
                                                    <ClearIndicator
                                                        {...props}
                                                        onTickClick={() => {
                                                            setTagsPopup({ ...tagsPopup, forceClose: true });
                                                            handleTagsSave(row);
                                                            setTimeout(() => setTagsPopup({ ...tagsPopup, forceClose: false }), 0);
                                                        }}
                                                    />
                                                ),
                                            }}
                                        />
                                        {
                                            (Object.keys(tagsArr).length === 0 ||
                                                Object.values(tagsArr).every(arr => arr.length === 0)) &&
                                            <MdOutlineClose
                                                onClick={() =>
                                                    setTagsPopup({
                                                        open: false,
                                                        rowId: null,
                                                        anchorPos: { x: 0, y: 0 },
                                                    })
                                                }
                                                style={{
                                                    fontSize: "18px",
                                                    position: "absolute",
                                                    right: "5px",
                                                    top: "13px",
                                                    zIndex: 5,
                                                    cursor: "pointer",
                                                }}
                                            />
                                        }
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {serviceItems.length > 0 ? (
                                    <div
                                        className="service-tasks-container"
                                        style={{ cursor: canEdit ? "pointer" : "default" }}
                                        onClick={canEdit ? (e) => handleTagsClick(e, row) : undefined}
                                    >
                                        {serviceItems.length === 1 ? (
                                            <span className="service-task">
                                                <MultiLineText
                                                    text={serviceItems[0]?.tag_name}
                                                    maxWidth={"90px"}
                                                />
                                            </span>
                                        ) : (
                                            <>
                                                <span className="first-task">
                                                    <MultiLineText
                                                        text={serviceItems[0]?.tag_name || "N/A"}
                                                        maxWidth={"90px"}
                                                    />
                                                </span>
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            <div>
                                                                {serviceItems
                                                                    .slice(1)
                                                                    .map((item, idx) => (
                                                                        <div
                                                                            key={idx}
                                                                            className="tooltip_item"
                                                                        >
                                                                            {item?.tag_name}
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span className="additional-tasks">
                                                        +{serviceItems.length - 1}
                                                    </span>
                                                </OverlayTrigger>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <button
                                        className="add_se_btn"
                                        onClick={canEdit ? (e) => handleTagsClick(e, row) : undefined}
                                        style={{ cursor: canEdit ? 'pointer' : 'default', opacity: canEdit ? 1 : 0.6 }}
                                    >
                                        {plusIcon} Add Tags
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                );
            },
            width: "170px",
        },
        {
            ...commonColumns,
            name: "Priority",
            key: "priority",
            modifyData: (row) => row?.priority,
            tableColumn: false,
            filterName: "priorityFilter",
            type: "text",
            width: "120px",
            selector: (row, index) => (
                <div style={{ display: "inline-block", overflow: "visible" }}>
                    <div
                        className={`priority ${row?.priority}`}
                        style={{ cursor: canEdit ? "pointer" : "default" }}
                        onClick={canEdit ? e => handlePriorityClick(e, row) : undefined}
                    >
                        <div className='d-flex align-items-center gap-1'>
                            <span>
                                {row?.priority === 'low' ? lowPriorityIcon : row?.priority === 'medium' ? mediumPriorityIcon : row?.priority === 'high' ? highPriorityIcon : null}
                            </span>
                            {row?.priority}
                        </div>

                        {
                            priorityPopup.open && priorityPopup.rowId === row.id &&
                            <PriorityPopup row={row} onSelect={handlePrioritySelect} index={index} />
                        }
                    </div>
                </div>
            ),
        },
        {
            ...commonColumns,
            name: "Meter Reading (km)",
            key: "odo_meter",
            selector: (row) => row?.odo_meter,
            modifyData: (row) => row?.odo_meter,
            tableColumn: false,
            filterName: "odoMeterFilter",
            type: "number",
            width: "170px",
        },
        {
            ...commonColumns,
            name: "Reporter",
            key: "reporter",
            selector: (row, index) => (
                <InlineUserCell
                    row={row}
                    index={index}
                    userField={row?.reporter}
                    type="reporter"
                    canEdit={false} // Reporters usually aren't clicked to edit like assignees
                    handleUserMouseEnter={handleUserMouseEnter}
                    handleUserMouseLeave={handleUserMouseLeave}
                    hoveredUser={hoveredUser}
                    showModal={showModal}
                    setShowModal={setShowModal}
                    handleCardMouseEnter={handleCardMouseEnter}
                    handleCardMouseLeave={handleCardMouseLeave}
                    setHoveredUser={setHoveredUser}
                />
            ),
            tableColumn: true,
            filterName: "reporterFilter",
            type: "text",
            minWidth: "230px",
            filterComponent: UserFilterDropdown,
            isMulti: true,
            options: userDropdownData,
            columnName: "Reporter",
        },
        {
            ...commonColumns,
            name: "Assignee",
            key: "assignee",
            selector: (row, index) => (
                <InlineUserCell
                    row={row}
                    index={index}
                    userField={row?.assignee}
                    type="assignee"
                    canEdit={canEdit}
                    handleUserMouseEnter={handleUserMouseEnter}
                    handleUserMouseLeave={handleUserMouseLeave}
                    handleUserClick={handleAssigneeClick}
                    hoveredUser={hoveredUser}
                    showModal={showModal}
                    setShowModal={setShowModal}
                    handleCardMouseEnter={handleCardMouseEnter}
                    handleCardMouseLeave={handleCardMouseLeave}
                    setHoveredUser={setHoveredUser}
                    dropdownState={assigneeDropdown}
                    dropdownComponent={UserDropdown}
                    dropdownProps={{
                        users: userDropdownData || [],
                        onSelect: handleAssigneeChange,
                        row: row,
                        dropdownRef: assigneeDropdownRef
                    }}
                />
            ),
            tableColumn: true,
            filterName: "assigneeFilter",
            type: "text",
            minWidth: "230px",
            filterComponent: UserFilterDropdown,
            isMulti: true,
            options: userDropdownData,
            columnName: "Assignee",
        },
        {
            ...commonColumns,
            name: "Service Entry",
            key: "service_entry",
            modifyData: (row) => row?.services,
            tableColumn: false,
            filterName: "servicesFilter",
            type: "text",
            width: "190px",
            selector: (row, index) => {
                const serviceItems = row?.services || [];

                if (serviceItems.length === 0) {
                    return (
                        <div style={{ display: "inline-block", overflow: "visible" }}>
                            <button className='add_se_btn' style={{ cursor: canEdit ? 'pointer' : 'default', opacity: canEdit ? 1 : 0.6 }} onClick={canEdit ? e => handleSEClick(e, row) : undefined}>{plusIcon} Add/Link SE</button>
                            {sePopup.open && sePopup.rowId === row.id &&
                                <AddSEPopup
                                    selected={selectedSE[row.id] || []}
                                    onCheck={handleSECheckbox}
                                    onDone={handleSEPopupDone}
                                    row={row}
                                    index={index}
                                />
                            }
                        </div>
                    );
                }

                if (serviceItems.length === 1) {
                    return (
                        <span className="service-task">
                            <MultiLineText text={serviceItems[0]?.service_id} maxWidth={"90px"} />
                        </span>
                    );
                }

                const firstItem = serviceItems[0]?.service_id || 'N/A';
                const remainingCount = serviceItems.length - 1;

                return (
                    <div className="service-tasks-container">
                        <span className="first-task">
                            <MultiLineText text={firstItem} maxWidth={"90px"} />
                        </span>

                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip>
                                    <div>
                                        {serviceItems.slice(1).map((item, idx) => (
                                            <div key={idx} className='tooltip_item'>{item?.service_id}</div>
                                        ))}
                                    </div>
                                </Tooltip>
                            }
                        >
                            <span className="additional-tasks">+{remainingCount}</span>
                        </OverlayTrigger>

                    </div>
                );
            },
        },
        {
            ...commonColumns,
            name: "Status",
            key: "status",
            selector: (row, index) => (
                <div style={{ display: "inline-block", overflow: "visible" }}>
                    <div
                        className={`status ${row?.status}`}
                        style={{ cursor: canEdit ? "pointer" : "default", textTransform: "capitalize" }}
                        onClick={canEdit ? e => handleStatusClick(e, row) : undefined}
                    >
                        <div className='d-flex align-items-center gap-1'>
                            {row?.status === "in_progress" ? inProgressIcon : row?.status === "pending" ? pendingIcon : row?.status === "open" ? openIcon : row?.status === "closed" ? closedIcon : row?.status === "resolved" ? resolvedIcon : row?.status === "completed" ? resolvedIcon : row?.status === "in_progress" ? inProgressIcon : null}
                            {row?.status === "in_progress" ? "In Progress" : row?.status}
                        </div>

                        {statusPopup.open && statusPopup.rowId === row.id &&
                            <StatusPopup row={row} onSelect={handleStatusSelect} index={index} />
                        }
                    </div>
                </div>
            ),
            tableColumn: true,
            filterName: "statusFilter",
            type: "text",
            width: "120px",
            filterComponent: FilterExternalDropdown,
            isMulti: true,
            options: issueStatusOptions
        },
        (canDelete) && {
            name: "Actions",
            key: "actions",
            checked: true,
            disabled: false,
            hide: false,
            exportColumn: false,
            allowOverflow: true,
            ignoreRowClick: true,
            selector: (row) => (
                <div className="table_action_btn">
                    <PermissionWrapper
                        module={MaintinaceSubModules.Issue}
                        permission={IssuePermission.Delete}
                        mainModule={AppMainModules.maintenance}
                    >
                        <button onClick={() => {
                            setDeleteUserPopup(true);
                            setDeleteUserId(row?.id);
                        }} className="delete_btn">
                            <RiDeleteBinLine />
                        </button>
                    </PermissionWrapper>

                </div>
            ),
            sortable: false,
            tableColumn: false,
            width: "120px",
        }
    ].filter(Boolean);

    const handleDeleteUser = async () => {
        try {
            await dispatch(deleteIssue(deleteUserId)).unwrap();
            successNotify('Issue deleted successfully');
            setDeleteUserPopup(false);
            setDeleteUserId(null);

            dispatch(fetchIssues({
                page: currentPage,
                limit: limit,
                searchFilters: searchFilters
            }));
        } catch (error) {
            errorNotify(error);
        }
    };

    const handleLimitChange = (newLimit) => {
        setCurrentPage(1);
        setLimit(newLimit);
    };

    const handleSearch = (text) => {
        setSearchText(text);
        setCurrentPage(1);
    };

    const exportPdfHandler = () => {
        const results = issuesData?.results
        const getIds = []
        results.forEach(item => {
            getIds.push(item.id)
        })

        const data = {
            service_issue_ids: getIds
        }

        dispatch(issuePdfExport(data)).unwrap()
            .then((response) => {
                const fileUrl = `${process.env.REACT_APP_API_FILES_URL}${response.data}`;
                const link = document.createElement("a");
                link.href = fileUrl;
                link.target = "_blank";
                link.setAttribute("download", `issue.pdf`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }).catch((error) => {
                errorNotify(error);
            })
    }

    const exportExcelHandler = () => {
        const results = issuesData?.results
        const getIds = []
        results.forEach(item => {
            getIds.push(item.id)
        })

        const data = {
            service_issue_ids: getIds
        }

        dispatch(issueExcelExport(data)).unwrap()
            .then((response) => {
                const fileUrl = `${process.env.REACT_APP_API_FILES_URL}${response.data}`;
                const link = document.createElement("a");
                link.href = fileUrl;
                link.target = "_blank";
                link.setAttribute("download", `issue.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }).catch((error) => {
                errorNotify(error);
            })
    }

    const processedResults = (issuesData?.results || []).map((item, idx, arr) => {
        const isLastFour = idx >= arr.length - 4;
        return { ...item, isLastFourServiceRow: isLastFour };
    });

    const handlePriorityClick = (e, row) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setPriorityPopup({
            open: true,
            rowId: row.id,
            anchorPos: {
                x: rect.right + 8,
                y: rect.top,
                left: rect.left,
                right: rect.right,
                top: rect.top,
                bottom: rect.bottom,
            }
        });
    };

    const handlePrioritySelect = async (priority, row) => {
        setTableLoading(true);
        try {
            await dispatch(updatePriorityApi({ service_issue_id: row.id, priority })).unwrap();
            await dispatch(fetchIssues({
                page: currentPage,
                limit: limit,
                search_text: searchText,
                searchFilters: searchFilters
            })).unwrap();
            await dispatch(getIssuesOverview()).unwrap();
            successNotify('Priority updated successfully');
            setPriorityPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
        } catch (error) {
            errorNotify(error);
        } finally {
            setTableLoading(false);
        }
    };

    const handleSEClick = (e, row) => {
        const rect = e.target.getBoundingClientRect();
        setSePopup({
            open: true,
            rowId: row.id,
            anchorPos: { x: rect.right + 8, y: rect.top }
        });
        setSelectedSE(prev => ({ ...prev, [row.id]: [] }));
    };

    const handleTagsClick = (e, row) => {
        const rect = e.target.getBoundingClientRect();
        setTagsPopup({
            open: true,
            rowId: row.id,
            anchorPos: { x: rect.right + 8, y: rect.top }
        });
    };

    const handleSECheckbox = (rowId, value) => {
        setSelectedSE(prev => {
            const current = prev[rowId] || [];
            if (current.includes(value)) {
                return { ...prev, [rowId]: current.filter(v => v !== value) };
            } else if (current.length < 10) {
                return { ...prev, [rowId]: [...current, value] };
            }
            return prev;
        });
    };

    const handleSEPopupDone = async (row) => {
        setTableLoading(true);
        const selected = selectedSE[row.id] || [];
        if (!selected.length) {
            setTableLoading(false);
            return;
        }
        const links = selected.map(service_id => ({
            service_id,
            service_issue_id: parseInt(row.id)
        }));
        try {
            await dispatch(createLinkIssueApi({ links })).unwrap();
            setSePopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
            setSelectedSE(prev => ({ ...prev, [row.id]: [] }));
            await dispatch(fetchIssues({
                page: currentPage,
                limit: limit,
                search_text: searchText,
                searchFilters: searchFilters
            }));
            successNotify('Service Entry linked successfully');
        } catch (error) {
            errorNotify(error);
        } finally {
            setTableLoading(false);
        }
    };

    const handleTagsChange = (selected, row) => {
        setTagsArr(prev => ({ ...prev, [row.id]: selected || [] }));
    };

    const handleTagsSave = async (row) => {
        const tags = (tagsArr[row.id] || []).map(tag => tag.value || tag.label || tag);
        setTableLoading(true);
        try {
            await dispatch(updateTagsApi({ service_issue_id: row.id, tags })).unwrap();

            const newTags = tagsArr[row.id];
            row.issue_tags = newTags.map(t => ({ tag_name: t.value })); // sync row data

            setTagsArr((prev) => ({
                ...prev,
                [row.id]: newTags, // overwrite with saved
            }));

            setTagsPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
            await dispatch(fetchIssues({
                page: currentPage,
                limit: limit,
                search_text: searchText,
                searchFilters: searchFilters
            }));
            successNotify('Tags updated successfully');
        } catch (error) {
            errorNotify(error);
        } finally {
            setTableLoading(false);
        }
    };

    const PriorityPopup = ({ row, onSelect }) => {
        return (
            <TablePopover
                innerRef={popupRef}
                anchorRect={priorityPopup.anchorPos}
                popupHeight={120}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                    {priorityOptions.map((priority) => (
                        <div key={priority.value} onClick={() => onSelect(priority?.value, row)}
                            style={{ display: "flex", alignItems: "center", padding: "5px 5px", gap: "10px", justifyContent: "center", backgroundColor: row.priority === priority?.value ? 'rgb(156 159 201 / 15%)' : "transparent" }}>
                            <div className={`priority ${priority?.value}`}>

                                <div className='d-flex align-items-center gap-1'>
                                    {priority?.value === 'low' ? lowPriorityIcon : priority?.value === 'medium' ? mediumPriorityIcon : priority?.value === 'high' ? highPriorityIcon : null}
                                    {priority.label}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </TablePopover>
        );
    }

    const AddSEPopup = ({ selected, onCheck, onDone, row, index }) => {
        const windowHeight = document.documentElement.clientHeight;
        const popupHeight = 160;

        const availableSpaceAbove = index * popupHeight;
        const availableSpaceBelow = windowHeight - (availableSpaceAbove + popupHeight);

        let positionStyle = {};
        if (availableSpaceBelow > availableSpaceAbove) {
            positionStyle = { top: "0px" };
        } else {
            positionStyle = { bottom: "0px" };
        }

        return (
            <div
                ref={sePopupRef}
                style={{
                    position: "absolute",
                    left: "120px",
                    background: "#FAF9FB",
                    border: "1px solid #D1D2E0",
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 4,
                    padding: "5px 0 4px 0",
                    minWidth: "200px",
                    // width: "200px",
                    ...positionStyle
                }}
            >
                <div style={{ padding: "0 8px 2px 8px", fontWeight: 600, fontSize: 13, color: "#3D3F5C", display: "flex", justifyContent: "space-between" }}>
                    <span>Selected</span>
                    <span style={{ fontWeight: 500 }}>{selected.length}/{row?.vehicle_services.length}</span>
                </div>
                <div style={{ maxHeight: 105, overflowY: "auto", padding: "0 0px 0 0px" }}>
                    {
                        row?.vehicle_services.length === 0 && (
                            <div style={{ padding: "8px", textAlign: "center", color: "#B4B4C6" }}>No SE Available</div>
                        )
                    }
                    {row?.vehicle_services.map(opt => (
                        <div key={opt.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 8px" }}>
                            <input
                                type="checkbox"
                                checked={selected.includes(opt.id)}
                                onChange={() => onCheck(row.id, opt.id)}
                                disabled={!selected.includes(opt.id) && selected.length >= 10}
                                style={{ accentColor: "#4014B8", cursor: "pointer" }}
                            />
                            <span style={{ fontSize: 14, color: "#3D3F5C" }}>{opt.service_id}</span>
                        </div>
                    ))}
                </div>
                {
                    row?.vehicle_services.length > 0 &&
                    <div style={{ padding: "0px 5px 0 5px" }}>
                        <button
                            style={{
                                width: "100%",
                                background: selected.length > 0 ? "#4014B8" : "#E5E5F2",
                                color: selected.length > 0 ? "#fff" : "#B4B4C6",
                                border: "none",
                                borderRadius: "6px",
                                padding: "4px 0",
                                fontWeight: 600,
                                fontSize: 14,
                                cursor: selected.length > 0 ? "pointer" : "not-allowed"
                            }}
                            disabled={selected.length === 0 || createLinkIssueLoading}
                            onClick={() => onDone(row)}
                        >
                            {createLinkIssueLoading ? 'Loading...' : "Done"}
                        </button>
                    </div>
                }
            </div>
        )
    }

    const StatusPopup = ({ row, onSelect }) => {
        return (
            <TablePopover
                innerRef={statusPopupRef}
                anchorRect={statusPopup.anchorPos}
                popupHeight={125}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                    {issueStatusOptions.map((status) => (
                        <div key={status.value}
                            style={{ display: "flex", alignItems: "center", padding: "5px 5px", gap: "10px", justifyContent: "center", backgroundColor: row.status === status?.value ? 'rgb(156 159 201 / 15%)' : "transparent" }}>
                            <div
                                className={`status ${status.value}`}
                                onClick={() => onSelect(status.value, row)}
                                style={{ cursor: "pointer", textTransform: "capitalize" }}>
                                <div className='d-flex align-items-center gap-1'>
                                    {status.value === "open" ? openIcon : status.value === "closed" ? closedIcon : status.value === "resolved" ? resolvedIcon : status.value === "completed" ? resolvedIcon : status.value === "in_progress" ? inProgressIcon : status.value === "in_progress" ? inProgressIcon : null}
                                    {status.label}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </TablePopover>
        )
    }

    const [search, setSearch] = useState('');
    const inputRef = useRef();

    useEffect(() => {
        const stopPropagation = (e) => e.stopPropagation();
        const inputElem = inputRef.current;
        if (inputElem) {
            inputElem.addEventListener('mousedown', stopPropagation);
        }
        return () => {
            if (inputElem) {
                inputElem.removeEventListener('mousedown', stopPropagation);
            }
        };
    }, []);

    const UserDropdown = ({ users, onSelect, row, dropdownRef }) => {
        const filteredUsers = users.filter(user =>
            user.label?.toLowerCase().includes(search.toLowerCase())
        );

        return (
            <div
                ref={dropdownRef}
                style={{
                    position: "relative",
                    background: "rgb(250, 249, 251)",
                    border: "1px solid #E0E0E0",
                    borderRadius: "12px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                    zIndex: 4,
                    width: "230px",
                    padding: "4px 0",
                }}
            >
                {/* Overlay when loading */}
                {updateAssigneeLoading && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: "rgba(255,255,255,0.6)",
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "not-allowed"
                        }}
                    >
                        <span style={{ color: "#4014B8", fontWeight: 600 }}>Updating...</span>
                    </div>
                )}
                <div style={{ maxHeight: 150, overflowY: "auto" }}>
                    {filteredUsers.map((user, idx) => (
                        <div
                            key={user.value}
                            onClick={updateAssigneeLoading ? () => { } : () => onSelect(user.value, row)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                padding: "2px 8px",
                                cursor: updateAssigneeLoading ? "not-allowed" : "pointer",
                                background: row?.assignee?.id === user.value ? "#fff" : "rgb(250, 249, 251)"
                            }}
                        >
                            <UserAvatar username={user.label} image={user.image} marginRight={"5px"} />
                            <span style={{ fontSize: 14, color: "#3D3F5C" }}>{user.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const handleUserMouseEnter = (e, row, type) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        const rect = e.currentTarget.getBoundingClientRect();

        if (!showModal) {
            setHoveredUser({
                show: true,
                rowId: row.id,
                type,
                anchorPos: {
                    x: rect.right + 6,
                    y: rect.bottom + 6,
                    left: rect.left,
                    right: rect.right,
                    top: rect.top,
                    bottom: rect.bottom,
                },
                locked: false
            });
        }
    };

    const handleUserMouseLeave = () => {
        hoverTimeoutRef.current = setTimeout(() => {
            if (!showModal) {
                setHoveredUser({ show: false, rowId: null, type: null, anchorPos: { x: 0, y: 0 }, locked: false });
            }
        }, 1500);
    };

    const handleUserClick = (e, row, type) => {
        e.stopPropagation();
        if (hoveredUser.locked && hoveredUser.rowId === row.id && hoveredUser.type === type) {
            setHoveredUser({ show: false, rowId: null, type: null, anchorPos: { x: 0, y: 0 }, locked: false });
        } else {
            const rect = e.currentTarget.getBoundingClientRect();

            if (!showModal) {
                setHoveredUser({
                    show: true,
                    rowId: row.id,
                    type,
                    anchorPos: {
                        x: rect.right + 6,
                        y: rect.bottom + 6,
                        left: rect.left,
                        right: rect.right,
                        top: rect.top,
                        bottom: rect.bottom,
                    },
                    locked: true
                });
            }
        }
    };

    const handleCardMouseEnter = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
    };

    const handleCardMouseLeave = () => {
        if (!showModal) {
            setHoveredUser({ show: false, rowId: null, type: null, anchorPos: { x: 0, y: 0 }, locked: false });
        }
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (!event.target.closest('.hyperlink')) {
                if (!showModal) {
                    setHoveredUser({ show: false, rowId: null, type: null, anchorPos: { x: 0, y: 0 }, locked: false });
                }
            }
        }
        if (hoveredUser.show && hoveredUser.locked) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [hoveredUser.show, hoveredUser.locked]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (reporterDropdownRef.current && !reporterDropdownRef.current.contains(event.target)) {
                setReporterDropdown({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
            }
            if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
                setAssigneeDropdown({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
            }
        }
        if (reporterDropdown.open || assigneeDropdown.open) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [reporterDropdown.open, assigneeDropdown.open]);

    const handleAssigneeClick = (e, row) => {
        const rect = e.target.getBoundingClientRect();
        setAssigneeDropdown({
            open: true,
            rowId: row.id,
            anchorPos: { x: rect.right + 8, y: rect.top }
        });
    };

    const handleAssigneeChange = async (userId, row) => {
        setTableLoading(true);
        try {
            await dispatch(updateAssigneeApi({ service_issue_id: row.id, assignee_id: userId })).unwrap();
            await dispatch(fetchIssues({
                page: currentPage,
                limit: limit,
                search_text: searchText,
                searchFilters: searchFilters
            }));
            successNotify('Assignee updated successfully');
            setAssigneeDropdown({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
        } catch (error) {
            errorNotify(error);
        } finally {
            setTableLoading(false);
        }
    };

    // Restore exportOptions definition
    const exportOptions = [
        {
            label: "CSV",
            icon: <BsFiletypeCsv />,
            onClick: exportExcelHandler,
            loading: issueExcelExportLoading,
            color: "green"
        },
        {
            label: "PDF",
            icon: <FaFilePdf />,
            onClick: exportPdfHandler,
            loading: issuePdfExportLoading,
            color: "red"
        }
    ];

    const handleStatusClick = (e, row) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setStatusPopup({
            open: true,
            rowId: row.id,
            anchorPos: {
                x: rect.right + 8,
                y: rect.top,
                left: rect.left,
                right: rect.right,
                top: rect.top,
                bottom: rect.bottom,
            }
        });
    };

    const handleStatusSelect = async (status, row) => {
        setTableLoading(true);
        try {
            await dispatch(updateStatusApi({ status, service_issue_id: row.id })).unwrap();
            setStatusPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
            await dispatch(fetchIssues({
                page: currentPage,
                limit: limit,
                search_text: searchText,
                searchFilters: searchFilters
            })).unwrap();
            await dispatch(getIssuesOverview()).unwrap();
            successNotify('Status updated successfully');
        } catch (err) {
            errorNotify(err);
        } finally {
            setTableLoading(false);
        }
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (!event.target.closest('.export_options') && !event.target.closest('.services_buttons button')) {
                setShowExportOption(false);
            }
        }
        if (showExportOption) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showExportOption]);

    return (
        <div>

            <Breadcrumbs values={['Issues', 'Overview']} />

            <CustomModal heading="Create Issue" size="lg" show={showCreateIssuePopup} handleClose={() => setShowCreateIssuePopup(false)}>
                <CreateIssue onClose={() => setShowCreateIssuePopup(false)}
                    searchText={searchText} searchFilters={searchFilters}
                    currentPage={currentPage} limit={limit}
                />
            </CustomModal>

            <CustomModal show={deleteUserPopup} handleClose={() => setDeleteUserPopup(false)} size="md" heading="Delete Issue" customHeaderCss="custom_header_css">
                <div className="delete_user_modal_body">
                    <p>Are you sure you want to delete this Issue?</p>

                    <Button
                        cancelBtn={() => setDeleteUserPopup(false)}
                        submitBtn={handleDeleteUser}
                        loading={deleteIssueLoading}
                        disabled={deleteIssueLoading}
                        buttonExtraCss="delete_user_modal_btn"
                        submitText="Delete"
                        type="submit"
                    />
                </div>
            </CustomModal>

            <div className='access_view'>
                <h2>Overview</h2>

                <div className='services_buttons'>
                    <Notification />

                    <div style={{ position: "relative" }}>
                        {
                            canExport &&
                            <button className='export_btn' onClick={() => setShowExportOption(!showExportOption)}>{exportDataIcon} Export Data</button>
                        }
                        {showExportOption && (
                            <div className='export_options'>
                                {exportOptions.map(option => (
                                    <span
                                        key={option.label}
                                        style={{ cursor: option.loading ? "not-allowed" : "pointer" }}
                                        onClick={option.loading ? null : option.onClick}
                                    >
                                        {option.icon} As {option.label}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <PermissionWrapper
                        module={MaintinaceSubModules.Issue}
                        permission={IssuePermission.Create}
                        mainModule={AppMainModules.maintenance}
                    >
                        <button onClick={() => setShowCreateIssuePopup(true)}>{plusIcon} Create Issue</button>
                    </PermissionWrapper>
                </div>
            </div>

            <IssuesBoxChart />

            <NewTableComponent
                showAddBtn={false}
                columns={columns}
                data={processedResults}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                limit={limit}
                setLimit={handleLimitChange}
                totalItems={issuesData?.meta?.totalItems}
                totalPages={issuesData?.meta?.totalPages}
                onSearch={handleSearch}
                loading={tableLoading ? false : issuesLoading}
                setSearchFilters={setSearchFilters}
                searchFilters={searchFilters}
                paginationLoading={tableLoading ? false : issuesLoading}
                tableClassName="maintenance_hover_table"
            />
        </div>
    )
}

export default Issues
